import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Form } from 'react-bootstrap';
import { ErrorMessage } from 'formik';
import PropTypes from 'prop-types';
import Avatar from '../Avatar';
import '../../assets/styles/react-select.scss';
import allMemberLogo from '../../assets/img/all-member-icon.png';
import { customUserFilter } from '../../helpers';

const MemberMultiSelectDropdown = ({
  id,
  labelText,
  name,
  fromArray,
  formProps,
  isDisabled,
  innerRef,
  isAllTeamMemberOption,
  isAllTeamMemberValuesWithNoArray = false,
  isRequiredField = false,
  requiredValueAs = 'email',
  showErrorMessage = false,
  onChangeAction,
  menuPlacement = 'auto',
}) => {
  // Optional "Select All" entry
  const selectAllOption = {
    value: 'all',
    label: (
      <>
        <div className="avatar avatar-xs me-3">
          <div
            className="avatar-img rounded-circle"
            style={{ backgroundImage: `url("${allMemberLogo}")` }}
          />
        </div>
        <span className="member-name">Select All</span>
      </>
    ),
    firstName: 'Select',
    lastName: 'All',
  };

  // Build dropdown options including Select All when enabled
  const dropdownOptions = [
    ...(isAllTeamMemberOption ? [selectAllOption] : []),
    ...fromArray.map((member) => ({
      value: requiredValueAs === 'email' ? member.email : member.userId,
      label: (
        <>
          <div className="avatar avatar-xs me-3">
            <Avatar
              firstName={member.firstName}
              lastName={member.lastName}
              imgPath={member.avatar ? member.avatar : ''}
            />
          </div>
          <span className="member-name">
            {member.lastName
              ? `${member.firstName} ${member.lastName}`
              : member.firstName}{' '}
            {member.employeeId && (
              <span className="text-muted">({member.employeeId})</span>
            )}
            {member.status === 'inactive' && (
              <span className="text-muted">{` (Inactive)`}</span>
            )}
          </span>
        </>
      ),
      firstName: member.firstName,
      lastName: member.lastName || '',
      employeeId: member.employeeId || '',
    })),
  ];

  const allValues = fromArray.map((i) => i.value);

  // Initialize UI selection from Formik
  const formikValues = formProps?.values?.[name] || [];
  const [localValues, setLocalValues] = useState(formikValues);

  // Keep the UI synced with Formik when external changes occur
  useEffect(() => {
    setLocalValues(formikValues);
  }, [formikValues]);

  const selectedOptions = dropdownOptions.filter((opt) => {
    if (opt.value === 'all') {
      return isAllTeamMemberOption && localValues.includes('all');
    }
    return localValues.includes(opt.value);
  });

  // Handles UI + Formik state changes
  const handleChange = (selected, actionMeta) => {
    const uiValues = selected?.map((o) => o.value) || [];
    const { action, option, removedValue } = actionMeta;
    const touchedOption = option || removedValue;
    let updated = [];

    const expandAll = () =>
      isAllTeamMemberValuesWithNoArray ? ['all'] : allValues;

    switch (action) {
      case 'select-option':
        if (touchedOption?.value === 'all') {
          updated = expandAll();
        } else {
          // If "all" was selected earlier and we select an individual item after that
          // eslint-disable-next-line no-lonely-if
          if (isAllTeamMemberValuesWithNoArray && localValues.includes('all')) {
            updated = [touchedOption.value];
          } else {
            updated = uiValues.filter((v) => v !== 'all');
          }
        }
        break;

      case 'remove-value':
      case 'deselect-option':
      case 'pop-value':
        if (touchedOption?.value === 'all') {
          updated = [];
        } else if (
          isAllTeamMemberValuesWithNoArray &&
          localValues.includes('all')
        ) {
          updated = allValues.filter((v) => v !== touchedOption.value);
        } else {
          updated = uiValues;
        }
        break;

      case 'clear':
        updated = [];
        break;

      default:
        updated = uiValues.includes('all') ? expandAll() : uiValues;
    }

    // When "all" should not persist as a stored value
    if (!isAllTeamMemberValuesWithNoArray) {
      updated = updated.filter((v) => v !== 'all');
    }

    setLocalValues(updated);

    formProps.setFieldValue(name, updated);
    if (onChangeAction) {
      onChangeAction(
        updated.includes('all') ? fromArray.map((item) => item.userId) : updated
      );
    }
  };

  return (
    <Form.Group controlId={id}>
      <Form.Label>
        {labelText} {isRequiredField && <span className="text-danger">*</span>}
      </Form.Label>
      <Select
        ref={innerRef}
        name={name}
        options={dropdownOptions}
        value={selectedOptions}
        onChange={handleChange}
        filterOption={customUserFilter}
        isMulti
        isClearable
        isSearchable
        isDisabled={isDisabled}
        menuPlacement={menuPlacement}
        className="basic-multi-select"
        classNamePrefix="select"
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#0649b5',
            neutral10: '#edf2f9',
            neutral20: '#d2ddec',
            neutral30: '#d2ddec',
            neutral50: '#b1c2da',
            neutral80: '#12263f',
            dangerLight: '#DEEBFF',
            danger: '#12263f',
          },
        })}
        placeholder="All"
      />
      {showErrorMessage && (
        <ErrorMessage
          name={name}
          render={(msg) => <small className="text-danger">{msg}</small>}
        />
      )}
    </Form.Group>
  );
};

MemberMultiSelectDropdown.propTypes = {
  id: PropTypes.string,
  fromArray: PropTypes.array,
  name: PropTypes.string,
  labelText: PropTypes.string,
  formProps: PropTypes.any,
  isDisabled: PropTypes.bool,
  isAllTeamMemberOption: PropTypes.bool,
  isAllTeamMemberValuesWithNoArray: PropTypes.bool,
  innerRef: PropTypes.any,
  isRequiredField: PropTypes.bool,
  requiredValueAs: PropTypes.string,
  showErrorMessage: PropTypes.bool,
  onChangeAction: PropTypes.func,
  menuPlacement: PropTypes.string,
};

export default MemberMultiSelectDropdown;
